//----------------------------------------------------
// Translate `update_state` strings coming from the AI
// into front-end actions.  Now supports route refresh.
//----------------------------------------------------

import { listMarkets } from 'store/actions/marketsActions';
import { setAiLabelFilter, setAiReport, setAiReportPageSize } from 'store/actions/appActions';

export default function routeUpdateState(flag, dispatch, clientId, router) {
  if (!flag) return;                       // "", null, undefined  ->  no-op

  // 0) Plain client-side navigation:  update_state: 'navigate:/labels/123'
  if (typeof flag === 'string' && flag.startsWith('navigate:')) {
    const path = flag.slice('navigate:'.length);
    if (router?.navigate && path) router.navigate(path);
    return;
  }

  // 1) Refresh a specific note detail
  if (typeof flag === 'string' && flag.startsWith('activity_note:')) {
    const id = Number(flag.split(':')[1]);
    window.dispatchEvent(new CustomEvent('refresh-activity-note', { detail: { id } }));
    return;
  }

  // 2) Launch AI label filter dialog
  //    update_state: 'launch_label_filter:{...}|navigate:/labels/123'
  if (typeof flag === 'string' && flag.startsWith('launch_label_filter:')) {
    try {
      const pipeIdx    = flag.indexOf('|navigate:');
      const jsonPart   = pipeIdx > -1 ? flag.slice('launch_label_filter:'.length, pipeIdx) : flag.slice('launch_label_filter:'.length);
      const navigateTo = pipeIdx > -1 ? flag.slice(pipeIdx + '|navigate:'.length) : null;
      const payload    = JSON.parse(jsonPart);
      // Full reset — clear ALL previous state before setting new payload
      // This prevents stale queryRows/currentQuery/tagId from a previous session bleeding through
      console.log('[updateStateRouter] launch_label_filter columns:', payload.columns?.length, 'rows:', payload.queryRows?.length);
      dispatch(setAiLabelFilter({
        open:          true,
        entityType:    null,
        mode:          null,
        namespaceName: null,
        tagName:       null,
        namespaceId:   null,
        tagId:         null,
        initialPrompt: null,
        criteria:      null,
        currentQuery:  null,
        queryRows:     undefined,
        queryCount:    0,
        columns:       [],
        saveTrigger:   false,
        savedResult:   null,
        ...payload,
      }));
      if (router?.navigate && navigateTo) router.navigate(navigateTo);
    } catch (e) {
      console.error('[routeUpdateState] Failed to parse launch_label_filter payload:', e);
    }
    return;
  }

  // 2b) Launch AI report — open the report page with built results
  //     update_state: 'launch_report:{...}|navigate:/reports'
  if (typeof flag === 'string' && flag.startsWith('launch_report:')) {
    try {
      const pipeIdx    = flag.indexOf('|navigate:');
      const jsonPart   = pipeIdx > -1 ? flag.slice('launch_report:'.length, pipeIdx) : flag.slice('launch_report:'.length);
      const navigateTo = pipeIdx > -1 ? flag.slice(pipeIdx + '|navigate:'.length) : null;
      const payload    = JSON.parse(jsonPart);
      dispatch(setAiReport({ open: true, ...payload }));
      if (router?.navigate && navigateTo) router.navigate(navigateTo);
    } catch (e) {
      console.error('[routeUpdateState] Failed to parse launch_report payload:', e);
    }
    return;
  }

  // 2c) Report rows-per-page — Key-driven page size change on the open report
  //     update_state: 'report_page_size:50'
  if (typeof flag === 'string' && flag.startsWith('report_page_size:')) {
    const n = parseInt(flag.slice('report_page_size:'.length), 10);
    if (Number.isFinite(n) && n > 0) dispatch(setAiReportPageSize(n));
    return;
  }

  // 3) Refine label filter — update query results or criteria
  if (typeof flag === 'string' && flag.startsWith('refine_label_filter:')) {
    try {
      const payload = JSON.parse(flag.slice('refine_label_filter:'.length));
      // New query engine format: { queryRows, queryCount, currentQuery }
      // Legacy criteria format: flat criteria object
      if (payload.queryRows !== undefined) {
        dispatch(setAiLabelFilter(payload));
      } else {
        dispatch(setAiLabelFilter({ criteria: payload }));
      }
    } catch (e) {
      console.error('[routeUpdateState] Failed to parse refine_label_filter payload:', e);
    }
    return;
  }

  // 4) Save label/org filter — trigger save from chatbot
  if (flag === 'save_label_filter' || flag === 'save_org_filter') {
    dispatch(setAiLabelFilter({ saveTrigger: true }));
    return;
  }

  // 5) Reopen filter dialog — force open without resetting state
  if (flag === 'reopen_label_filter') {
    dispatch(setAiLabelFilter({ open: true }));
    return;
  }

  /* ------------------------------------------------------------------
   * Registry : map back-end strings  →  front-end side-effects
   * ------------------------------------------------------------------ */
  const registry = {
    markets: () => dispatch(listMarkets(clientId, { force: true })),
    activity_notes: () =>
      window.dispatchEvent(new Event('refresh-activity-notes')),
    '': () => {},
    routermeunused: () => {
      const nav = router?.navigate;
      if (typeof nav === 'function') {
        nav(0);
      } else {
        window.location.reload();
      }
    },
  };

  const fn = registry[flag];
  if (typeof fn === 'function') fn();
}